/**
 * What the page holds of a party (ADR-101 decision 19): the roster the gateway sends whole at every change and the
 * requests a player makes of it. Plain data, so the connection, the store and the panels share it without importing
 * one another.
 */

/**
 * One seat of the roster as the gateway sends it: its place 0 to 5, the character's two names, whether it leads,
 * whether it may invite, whether it is online and the id of the map it stands on (empty while offline).
 */
export interface PartyMember {
  readonly place: number;
  readonly firstName: string;
  readonly familyName: string;
  readonly leader: boolean;
  readonly mayInvite: boolean;
  readonly online: boolean;
  readonly map: string;
}

/**
 * A request a player makes of the party: invite a character by its names, answer an invitation by its id, leave, and
 * for the leader remove a member, hand on the lead, and give or take back a member's right to invite.
 */
export type PartyRequest =
  | { readonly kind: "partyInvite"; readonly firstName: string; readonly familyName: string }
  | { readonly kind: "partyAnswer"; readonly invite: number; readonly accept: boolean }
  | { readonly kind: "partyLeave" }
  | { readonly kind: "partyRemove"; readonly firstName: string; readonly familyName: string }
  | { readonly kind: "partyPromote"; readonly firstName: string; readonly familyName: string }
  | { readonly kind: "partyGrant"; readonly firstName: string; readonly familyName: string; readonly mayInvite: boolean };

/**
 * A character's two names.
 */
export interface PartyName {
  readonly firstName: string;
  readonly familyName: string;
}

/**
 * Whether two names are one character's, ignoring case, as the gateway compares them.
 *
 * @param a One name.
 * @param b The other.
 * @returns True when both parts are equal.
 */
export function sameName(a: PartyName, b: PartyName): boolean {
  return a.firstName.toLowerCase() === b.firstName.toLowerCase() && a.familyName.toLowerCase() === b.familyName.toLowerCase();
}
